/**
 * FAQ Data — Restore.Dental
 * 
 * Centralized FAQ content for use in components and structured data.
 * 
 * RULES:
 * - Keep answers ≤ 350 characters for optimal SERP display
 * - Only include FAQs that are VISIBLE on the page
 * - Don't use FAQ schema on transactional pages
 */

// ============================================================================
// HOMEPAGE FAQs
// ============================================================================

export const homepageFAQs = [
  {
    question: "Do you accept new patients?",
    answer: "Yes! We're currently accepting new patients. We often have same-day appointments available for emergencies, and we can usually book new patient consultations within 48 hours."
  },
  {
    question: "What if I haven't been to the dentist in years?",
    answer: "No judgment here! We understand life gets busy and dental anxiety is real. We specialize in making patients feel comfortable, especially those who haven't visited in a while. We'll take things at your pace."
  },
  {
    question: "Do you offer payment plans?",
    answer: "Yes! We offer flexible payment plans to make dental care affordable. We can discuss options during your consultation, including 0% finance for larger treatments like Invisalign. Calculate your monthly payment at restore.dental/finance."
  },
  {
    question: "Is Invisalign really as good as traditional braces?",
    answer: "For most cases, yes! Dr. Kartik Patel is an experienced Invisalign provider with 150+ cases completed. During your free consultation, we'll assess if Invisalign is right for you and show you a digital preview of your potential results."
  },
];

// ============================================================================
// INVISALIGN PAGE FAQs
// ============================================================================

export const invisalignFAQs = [
  {
    question: "How long does Invisalign treatment take?",
    answer: "Most cases take 6-18 months. The exact timeline depends on your individual needs. We'll give you a precise estimate during your free consultation with a digital treatment preview."
  },
  {
    question: "Will Invisalign hurt?",
    answer: "You may feel pressure when you switch to new aligners (every 1-2 weeks), but it's much more comfortable than traditional braces. Most patients adjust within a few days and find it pain-free."
  },
  {
    question: "How much does Invisalign cost?",
    answer: "Treatment typically costs £2,500-£5,500 depending on complexity. We offer 0% interest-free finance from 3-60 months to spread the cost. Book a free consultation for a personalized quote."
  },
  {
    question: "Can I eat normally with Invisalign?",
    answer: "Yes! Unlike traditional braces, you remove the aligners to eat and drink (except water). You can enjoy all your favorite foods without restrictions. Just brush your teeth before putting the aligners back in."
  },
  {
    question: "How often do I need appointments?",
    answer: "Typically every 6-8 weeks for progress checks. Appointments are quick (15-30 minutes) and we'll provide multiple aligner sets so you can progress at home between visits."
  },
];

// ============================================================================
// DENTAL IMPLANTS PAGE FAQs
// ============================================================================

export const implantsFAQs = [
  {
    question: "Do dental implants hurt?",
    answer: "The procedure is done under local anesthetic, so you won't feel pain during treatment. Most patients report less discomfort than a tooth extraction. We provide pain management guidance for the healing period."
  },
  {
    question: "How long do dental implants last?",
    answer: "With proper care, dental implants can last 20-30 years or even a lifetime. They have a 95%+ success rate and are the most durable tooth replacement option available."
  },
  {
    question: "Am I too old for dental implants?",
    answer: "Age is rarely a barrier. As long as you have adequate bone density and good general health, implants can work at any age. We've successfully placed implants in patients from 20s to 80s."
  },
  {
    question: "How much do dental implants cost?",
    answer: "Single implants start from £2,295. Full mouth reconstruction varies by complexity. We offer 0% finance plans from £191/month. Book a free consultation for a personalized treatment plan and quote."
  },
];

// ============================================================================
// COMPOSITE BONDING PAGE FAQs
// ============================================================================

export const compositeBondingFAQs = [
  {
    question: "How long does composite bonding last?",
    answer: "With proper care, bonding typically lasts 5-10 years. Avoiding hard foods, not biting nails, and maintaining good oral hygiene will extend the lifespan. Touch-ups are simple and affordable."
  },
  {
    question: "Does composite bonding damage your teeth?",
    answer: "No! It's a minimally invasive procedure. Unlike veneers, we don't remove tooth structure. The composite is bonded directly to your existing tooth, and the process is completely reversible."
  },
  {
    question: "Can I get composite bonding in one appointment?",
    answer: "Yes! Most cases are completed in a single 1-2 hour appointment. You'll walk out with your new smile the same day. For multiple teeth, we may schedule 2-3 sessions for optimal results."
  },
  {
    question: "Will composite bonding stain?",
    answer: "The material is stain-resistant but not stain-proof. Avoiding coffee, red wine, and smoking helps maintain the color. Professional cleanings every 6 months keep bonding looking fresh."
  },
];

// ============================================================================
// COSMETIC DENTISTRY PAGE FAQs
// ============================================================================

export const cosmeticDentistryFAQs = [
  {
    question: "What's the difference between veneers and bonding?",
    answer: "Veneers are thin porcelain shells that cover the entire front of the tooth. Bonding uses tooth-colored resin applied directly. Veneers last longer (10-20 years) but bonding is more affordable and reversible."
  },
  {
    question: "How much does a smile makeover cost?",
    answer: "It varies based on treatments needed. Comprehensive makeovers range £5,000-£15,000. We create custom plans to fit your goals and budget, with 0% finance available. Book a free consultation for your personalized quote."
  },
  {
    question: "Is teeth whitening safe?",
    answer: "Yes! Professional whitening is completely safe when done correctly. We use dentist-grade products that are stronger yet safer than over-the-counter options. Results last 1-3 years with good care."
  },
];

// ============================================================================
// EMERGENCY DENTISTRY PAGE FAQs
// ============================================================================

export const emergencyDentistryFAQs = [
  {
    question: "Do you offer same-day emergency appointments?",
    answer: "Yes! We reserve slots daily for emergencies. Call us at 020 8993 2233 as early as possible, and we'll do our best to see you the same day, even on weekends."
  },
  {
    question: "What counts as a dental emergency?",
    answer: "Severe toothache, knocked-out tooth, broken tooth with sharp edges, lost filling/crown, abscess, or uncontrolled bleeding. If you're in pain or worried, call us - we'll advise if you need immediate care."
  },
  {
    question: "What should I do if I knock out a tooth?",
    answer: "Find the tooth, hold it by the crown (not root), rinse gently with water, try to place it back in the socket, or keep it in milk. Call us immediately - we have the best chance of saving it within 1 hour."
  },
];

// ============================================================================
// GENERAL DENTISTRY PAGE FAQs
// ============================================================================

export const generalDentistryFAQs = [
  {
    question: "How often should I visit the dentist?",
    answer: "Most patients should visit every 6 months for checkups and cleaning. If you have gum disease, dental work, or other issues, we may recommend more frequent visits (every 3-4 months)."
  },
  {
    question: "Do you accept dental insurance?",
    answer: "Yes! We accept most major dental insurance plans. We'll handle the paperwork and help you maximize your benefits. Even without insurance, we offer affordable private rates and payment plans."
  },
  {
    question: "What's included in a checkup?",
    answer: "A thorough examination of teeth, gums, and mouth; X-rays (if needed); oral cancer screening; plaque/tartar removal; polish; and personalized treatment recommendations. Appointments typically take 30-45 minutes."
  },
];

// ============================================================================
// FINANCE PAGE FAQs
// ============================================================================

export const financeFAQs = [
  {
    question: "Who is eligible for finance?",
    answer: "You must be over 18, a UK resident, and earn a regular income. Credit decisions are subject to your individual circumstances and affordability checks."
  },
  {
    question: "Will applying affect my credit score?",
    answer: "The initial application uses a soft credit check, which won't affect your score. Only if you accept the finance offer will a hard credit check be performed."
  },
  {
    question: "How much can I borrow?",
    answer: "Finance is available for treatments costing between £500 and £30,000, subject to approval."
  },
  {
    question: "Can I pay off my loan early?",
    answer: "Yes! There are no early repayment charges. You can clear your balance at any time without penalty."
  },
  {
    question: "What treatments are covered?",
    answer: "Finance is available for most dental treatments including Invisalign, dental implants, cosmetic bonding, crowns, and comprehensive smile makeovers."
  },
];

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  homepageFAQs,
  invisalignFAQs,
  implantsFAQs,
  compositeBondingFAQs,
  cosmeticDentistryFAQs,
  emergencyDentistryFAQs,
  generalDentistryFAQs,
  financeFAQs,
};
